
import ROUTES from '../routes/router-datas';
import {matchOneRoleFunction} from './dataHelper';

interface I_RESOLVE_ACCESS_OPTIONS {
  requiredEndpoints?: string[];
  rolesPerEndpoint?: Record<string,string[]>;
  role?: string;
  fonction?: string;
  fallbackRequireRoleFunction?: string[];
  fallbackRequireRole?: string[];
  debugLabel?: string;
}

interface I_RESOLVE_ACCESS_RESULT {
  allowed: boolean;
  mode: 'no-constraint' | 'endpoint-map' | 'fallback';
  requiredRolesFromEndpoints: string[];
  knownEndpointsCount: number;
}

/**
 * Résout l'autorisation d'accès en priorité via roles_per_endpoint (Hello),
 * puis applique un fallback legacy (requireRoleFunction / requireRole) si besoin.
 *
 * Ce helper est partagé entre le contrôle de route et le menu pour éviter les divergences.
 */
const resolveAccessByEndpoints = ({
  requiredEndpoints = [],
  rolesPerEndpoint,
  role,
  fonction,
  fallbackRequireRoleFunction = [],
  fallbackRequireRole = [],
  debugLabel = 'route-access',
}:I_RESOLVE_ACCESS_OPTIONS):I_RESOLVE_ACCESS_RESULT => {
  if (!requiredEndpoints || requiredEndpoints.length === 0) {
    console.debug(`[${debugLabel}] no endpoint constraint -> allow`);
    return {
      allowed: true,
      mode: 'no-constraint',
      requiredRolesFromEndpoints: [],
      knownEndpointsCount: 0,
    };
  }

  const hasEndpointMatrix = !!rolesPerEndpoint && Object.keys(rolesPerEndpoint).length > 0;
  if (hasEndpointMatrix) {
    const requiredRoles = new Set<string>();
    let knownEndpointsCount = 0;
    let hasOpenEndpoint = false;

    requiredEndpoints.forEach((endpoint:string) => {
      if (!rolesPerEndpoint || !(endpoint in rolesPerEndpoint)) {
        return;
      }
      knownEndpointsCount++;
      const endpointRoles = rolesPerEndpoint[endpoint] || [];
      if (endpointRoles.length === 0) {
        hasOpenEndpoint = true;
        return;
      }
      endpointRoles.forEach((r:string) => requiredRoles.add(r));
    });

    if (hasOpenEndpoint) {
      console.debug(`[${debugLabel}] endpoint-map contains an open endpoint -> allow`,{requiredEndpoints});
      return {
        allowed: true,
        mode: 'endpoint-map',
        requiredRolesFromEndpoints: [],
        knownEndpointsCount,
      };
    }

    const requiredRolesFromEndpoints = Array.from(requiredRoles);
    if (requiredRolesFromEndpoints.length > 0) {
      const allowed = matchOneRoleFunction({
        requireRoleFunction: requiredRolesFromEndpoints,
        role,
        fonction,
      });
      console.debug(`[${debugLabel}] endpoint-map roles evaluation`,{
        requiredEndpoints,
        requiredRolesFromEndpoints,
        allowed,
      });
      return {
        allowed,
        mode: 'endpoint-map',
        requiredRolesFromEndpoints,
        knownEndpointsCount,
      };
    }

    // Cas matrice fournie mais sans endpoint connu: on bascule en fallback
    // pour rester cohérent avec le comportement legacy.
    console.debug(`[${debugLabel}] endpoint-map provided but no known endpoints -> fallback`,{requiredEndpoints});
  }

  if (fallbackRequireRoleFunction && fallbackRequireRoleFunction.length > 0) {
    const allowed = matchOneRoleFunction({
      requireRoleFunction: fallbackRequireRoleFunction,
      role,
      fonction,
    });
    console.debug(`[${debugLabel}] fallback requireRoleFunction evaluation`,{fallbackRequireRoleFunction,allowed});
    return {
      allowed,
      mode: 'fallback',
      requiredRolesFromEndpoints: [],
      knownEndpointsCount: 0,
    };
  }

  if (fallbackRequireRole && fallbackRequireRole.length > 0) {
    const allowed = matchOneRoleFunction({
      requireRoleFunction: fallbackRequireRole,
      role,
      fonction,
    });
    console.debug(`[${debugLabel}] fallback requireRole evaluation`,{fallbackRequireRole,allowed});
    return {
      allowed,
      mode: 'fallback',
      requiredRolesFromEndpoints: [],
      knownEndpointsCount: 0,
    };
  }

  console.debug(`[${debugLabel}] fallback with no role constraint -> allow`);
  return {
    allowed: true,
    mode: 'fallback',
    requiredRolesFromEndpoints: [],
    knownEndpointsCount: 0,
  };
};

const readUrlParam = (name:string):string | undefined => {
  try{
    const fullQueries = document.location.search;
    if (fullQueries && fullQueries !== '?') {
      const queries = fullQueries.replace('?','').split('&');
      let result = undefined;
      queries.forEach(q => {
        const key = q.split('=')[0];
        if (key === name) {
          result = q.split('=')[1];
        }
      });
      return result;
    }
    return undefined;
  }catch(err){
    console.error(`Cannot read URL param ${name}`,err);
    return undefined;
  }
};

/**
 * Récupère une ancre d'url (suffix de l'ancre)
 * @param name
 * @returns
 */
const readUrlAnchorValue = (name:string):string | undefined => {
  try{
    const fullAnchor = document.location.hash;
    const anchors = !!fullAnchor ? fullAnchor.replace('#','').split('!!') : [];
    const matchingAnchors = anchors.filter(a => a.startsWith(name));
    if (matchingAnchors.length > 0) {
      // on prend que le premier
      const results = new RegExp(`${name }([^&#]*)`).exec(matchingAnchors[0]);
      if (results === undefined || results === null) {
        return undefined;
      }
      return decodeURI(results[1]) || undefined;
    }
    else {
      return undefined;
    }
  }catch(err){
    console.error(`Cannot read URL anchor ${name}`,err);
    return undefined;
  }
};

/**
 * Détermine si on a une ancre d'URL
 * @param name
 * @returns
 */
const isUrlAnchor = (name:string):boolean => {
  try{
    const fullAnchor = document.location.hash;
    const anchors = !!fullAnchor ? fullAnchor.replace('#','').split('!!') : [];
    const matchingAnchors = anchors.filter(a => a.startsWith(name));
    if (matchingAnchors.length > 0) {
      // on prend que le premier
      const results = new RegExp(`${name }`).exec(fullAnchor);
      if (results === undefined || results === null) {
        return false;
      }
      return true;
    }
    else {
      return false;
    }
  }catch(err){
    console.error(`Cannot read URL anchor ${name}`,err);
    return false;
  }
};

/**
 * Met une ancre d'url
 * @param name
 * @param value
 */
const setUrlAnchor = (name:string,value:string) => {
  try{
    const fullAnchor = document.location.hash;
    const anchors = !!fullAnchor ? fullAnchor.replace('#','').split('!!') : [];
    const nomatchingAnchors = anchors.filter(a => !a.startsWith(name));
    nomatchingAnchors.push(name+value);
    document.location.hash = "#" + nomatchingAnchors.join('!!');
  }catch(err){
    console.error(`Cannot set URL anchor ${name}`,err);
  }
};

/**
 * Extrait le numero de page depuis l'ancre d'url
 * @returns
 */
const getPage = ():number => {
  const pageAnchor = readUrlAnchorValue('page');
  return pageAnchor !== undefined ? parseInt(pageAnchor) || 1 : 1;
};

/**
 * Extrait l'onglet depuis l'ancre d'url
 * @returns
 */
const getTab = ():string => {
  const tabAnchor = readUrlAnchorValue('tab');
  return tabAnchor || 'collaborators';
};

/**
 * Extrait l'origine depuis l'ancre d'url
 * @returns
 */
const getOrigin = ():string => {
  const originAnchor = readUrlAnchorValue('origin=');
  return originAnchor || '';
};


// ----------------------------------
// Transmission des filtres depuis le plan-de-charges vers la vue des activités via l'ancre du lien,
// qu'on peut ouvrir dans un nouvel onglet :
// de la forme filtersactivities=filtre1:valeur1,valeur2;filtre2:valeur3

interface I_NAVIGATION_FILTERS_ACTIVITIES {
  collaborators?: string
  from_date?: string
  to_date?: string
  activity_types?: string
  directions_hr?: string
  hubs_hr?: string
  skills?: string
  functions?: string
  of_number?: string
  billing_customers?: string
  audited_customers?: string
  referentials?: string
  referential_types?: string
}

/**
 * Genere l'ancre d'url contenant les filtres d'activites
 */
const createFiltersActivitiesAnchor = ({
  collaborators,
  from_date,
  to_date,
  activity_types,
  directions_hr,
  hubs_hr,
  skills,
  functions,
  of_number,
  billing_customers,
  audited_customers,
  referentials,
  referential_types,
}:{
  collaborators?: number[],
  from_date?: string,
  to_date?: string,
  activity_types?: number[],
  directions_hr?: string[],
  hubs_hr?: string[],
  skills?: number[],
  functions?: string[],
  of_number?: string,
  billing_customers?: number[],
  audited_customers?: number[],
  referentials?: number[],
  referential_types?: string[],
}):string => {
  const f_collaborators = collaborators && collaborators.length > 0 ? `collaborators:${collaborators.join(',')}` : '';
  const f_from_date = from_date ? `from_date:${from_date}` : '';
  const f_to_date = to_date ? `to_date:${to_date}` : '';
  const f_activity_types = activity_types && activity_types.length > 0 ? `activity_types:${activity_types.join(',')}` : '';
  const f_directions_hr = directions_hr && directions_hr.length > 0 ? `directions_hr:${directions_hr.join(',')}` : '';
  const f_hubs_hr = hubs_hr && hubs_hr.length > 0 ? `hubs_hr:${hubs_hr.join(',')}` : '';
  const f_skills = skills && skills.length > 0 ? `skills:${skills.join(',')}` : '';
  const f_functions = functions && functions.length > 0 ? `functions:${functions.join(',')}` : '';
  const f_of_number = of_number ? `of_number:${of_number}` : '';
  const f_billing_customers = billing_customers && billing_customers.length > 0 ? `billing_customers:${billing_customers.join(',')}` : '';
  const f_audited_customers = audited_customers && audited_customers.length > 0 ? `audited_customers:${audited_customers.join(',')}` : '';
  const f_referentials = referentials && referentials.length > 0 ? `referentials:${referentials.join(',')}` : '';
  const f_referential_types = referential_types && referential_types.length > 0 ? `referential_types:${referential_types.join(',')}` : '';

  const all = [];
  if (f_collaborators && f_collaborators !== '') {
    all.push(f_collaborators);
  }
  if (f_from_date && f_from_date !== '') {
    all.push(f_from_date);
  }
  if (f_to_date && f_to_date !== '') {
    all.push(f_to_date);
  }
  if (f_activity_types && f_activity_types !== '') {
    all.push(f_activity_types);
  }
  if (f_directions_hr && f_directions_hr !== '') {
    all.push(f_directions_hr);
  }
  if (f_hubs_hr && f_hubs_hr !== '') {
    all.push(f_hubs_hr);
  }
  if (f_skills && f_skills !== '') {
    all.push(f_skills);
  }
  if (f_functions && f_functions !== '') {
    all.push(f_functions);
  }
  if (f_of_number && f_of_number !== '') {
    all.push(f_of_number);
  }
  if (f_billing_customers && f_billing_customers !== '') {
    all.push(f_billing_customers);
  }
  if (f_audited_customers && f_audited_customers !== '') {
    all.push(f_audited_customers);
  }
  if (f_referentials && f_referentials !== '') {
    all.push(f_referentials);
  }
  if (f_referential_types && f_referential_types !== '') {
    all.push(f_referential_types);
  }
  return `filtersactivities=${all.join(';')}`;
}

/**
 * Extrait les filtres à appliquer depuis l'ancre d'url
 */
const parseFiltersActivities = () => {
  const filtersActivities = getFiltersActivities();
  const parseNumberArray = (anchorValue:string|undefined) => {
    // undefined si vide
    const tmp1 = anchorValue && anchorValue !== '' ? anchorValue : undefined;
    return tmp1?.split(',')?.map((v:string) => parseInt(v)) || undefined;
  }
  const parseStringArray = (anchorValue:string|undefined) => {
    // undefined si vide
    const tmp1 = anchorValue && anchorValue !== '' ? anchorValue : undefined;
    return tmp1?.split(',') || undefined;
  }
  return {
    from_date: filtersActivities?.from_date || '',
    to_date: filtersActivities?.to_date || '',
    collaborators: parseNumberArray(filtersActivities?.collaborators),
    activity_types: parseNumberArray(filtersActivities?.activity_types),
    directions_ops: [],
    hubs_ops: [],
    directions_hr: parseStringArray(filtersActivities?.directions_hr),
    hubs_hr: parseStringArray(filtersActivities?.hubs_hr),
    skills: parseNumberArray(filtersActivities?.skills),
    functions: parseStringArray(filtersActivities?.functions),
    billing_customers: parseNumberArray(filtersActivities?.billing_customers),
    audited_customers: parseNumberArray(filtersActivities?.audited_customers),
    referentials: parseNumberArray(filtersActivities?.referentials),
    referential_types: parseStringArray(filtersActivities?.referential_types),
    of_number: filtersActivities?.of_number || ''
  };
}

/**
 * Extrait les filtres depuis l'ancre d'url
 * de la forme filtersactivities=collaborators:${item.id};from_date:${monthStart}
 * @returns
 */
const getFiltersActivities = ():I_NAVIGATION_FILTERS_ACTIVITIES => {
  const filtersAnchor = readUrlAnchorValue('filtersactivities=');
  if (filtersAnchor) {
    const filtersTypes = filtersAnchor.split(';');
    return filtersTypes.reduce((acc:any, elem) => {
      const kv = elem.split(':');
      const key:string = kv[0];
      const value:string = kv[1];
      acc[key] = value;
      return acc;
    }, {});
  }
  return {};
};

/**
 * Détermine si on charge le plan de charge en mode preview (postpone) depuis l'ancre d'url
 * @returns
 */
const isPreviewMode = ():boolean => {
  return readUrlAnchorValue('preview') !== undefined;
};

/**
 * Extrait les from et to dates de l'ancre preview
 * @returns
 */
const getPreviewDates = ():{from_date:string,to_date:string}|undefined => {
  const previewAnchor = readUrlAnchorValue('preview_');
  if (!previewAnchor) {
    return undefined;
  }
  else {
    previewAnchor.split('_');
    // TODO SECURISER LES VALEURS
    return {
      from_date: previewAnchor.split('_')[0],
      to_date: previewAnchor.split('_')[1],
    };
  }
};

/**
 * Récupère les données associées à la route
 * @param param0
 */
const getRouteData = ({route}:{route:string}) => {
  let matchingRouteData = ROUTES[route];
  if (matchingRouteData === undefined) {
    // on remplace le 2e /xxx par :id
    // pageRoute est de la forme /users/2/details {0}/{1}/{2}/{3}
    const splited = route.split('/');
    let genericRoute = `${splited[0]}/${splited[1]}`;
    if (splited.length > 2) {
      genericRoute += '/:id';
      if (splited.length > 3) {
        genericRoute += '/' + splited[3];
        // NB a priori on s'arrete a 3... a voir selon la creation des routes
      }
    }
    matchingRouteData = ROUTES[genericRoute];
  }
  return matchingRouteData;
};

export {
  readUrlParam,
  readUrlAnchorValue,
  isUrlAnchor,
  getPage,
  createFiltersActivitiesAnchor,
  getFiltersActivities,
  parseFiltersActivities,
  isPreviewMode,getPreviewDates,getRouteData,getTab,setUrlAnchor,getOrigin,
  resolveAccessByEndpoints,
};
